// Eightfold Path — navigation framework for the Living Constellation.
// Each spoke is a category of practice, not a claim of attainment.
// Every ecosystem node maps to one spoke via kind + tag heuristics so the
// constellation reorganizes around whichever spoke the user selects.

import type { EcosystemItem, ItemKind } from "./ecosystem";

export type SpokeId =
  | "view"
  | "intention"
  | "speech"
  | "action"
  | "livelihood"
  | "effort"
  | "mindfulness"
  | "concentration";

export interface Spoke {
  id: SpokeId;
  name: string;         // "Right View"
  short: string;        // "View"
  essence: string;      // one-line subtitle
  angle: number;        // degrees, 0 = top, clockwise
  hue: "gold" | "ivory" | "emerald" | "violet";
}

// Ordered clockwise from the top. Angles in degrees where 0 = north.
export const SPOKES: readonly Spoke[] = [
  { id: "view",          name: "Right View",          short: "View",          essence: "Understanding · Books · Foundational teachings", angle:   0, hue: "gold" },
  { id: "intention",     name: "Right Intention",     short: "Intention",     essence: "Purpose · Daily reflection · Morning activations", angle:  45, hue: "gold" },
  { id: "speech",        name: "Right Speech",        short: "Speech",        essence: "Music · Lyrics · Poetry · Creative expression",    angle:  90, hue: "ivory" },
  { id: "action",        name: "Right Action",        short: "Action",        essence: "Habits · Practices · Challenges",                  angle: 135, hue: "emerald" },
  { id: "livelihood",    name: "Right Livelihood",    short: "Livelihood",    essence: "Business · Craft · Creative work",                 angle: 180, hue: "gold" },
  { id: "effort",        name: "Right Effort",        short: "Effort",        essence: "Discipline · Training · Consistency",              angle: 225, hue: "emerald" },
  { id: "mindfulness",   name: "Right Mindfulness",   short: "Mindfulness",   essence: "Meditation · Presence · Breath · Reading mode",    angle: 270, hue: "violet" },
  { id: "concentration", name: "Right Concentration", short: "Concentration", essence: "Deep focus · Quiet room · Study",                  angle: 315, hue: "violet" },
] as const;

const SPOKE_BY_ID: Record<SpokeId, Spoke> = Object.fromEntries(SPOKES.map((s) => [s.id, s])) as Record<SpokeId, Spoke>;
export function getSpoke(id: SpokeId): Spoke { return SPOKE_BY_ID[id]; }

/** Deterministic mapping from an ecosystem item to its spoke. */
export function spokeForItem(item: EcosystemItem): SpokeId {
  const tags = new Set(item.tags);
  const hay = (item.title + " " + item.subtitle + " " + (item.body ?? "")).toLowerCase();
  const has = (...needles: string[]) => needles.some((n) => tags.has(n) || hay.includes(n));

  if (item.kind === "book") return "view";
  if (item.kind === "music") return "speech";

  if (item.kind === "activation") {
    if (has("meditation", "breath", "presence", "stillness")) return "mindfulness";
    if (has("focus", "concentration", "study", "deep")) return "concentration";
    if (has("morning", "intention", "reflection", "vision")) return "intention";
    if (has("discipline", "training", "consistency", "practice")) return "effort";
    return "action";
  }

  // Wisdom teachings
  if (has("livelihood", "business", "work", "money", "sovereignty", "craft")) return "livelihood";
  if (has("meditation", "presence", "breath", "mindful")) return "mindfulness";
  if (has("focus", "concentration", "study")) return "concentration";
  if (has("discipline", "training", "consistency", "effort")) return "effort";
  if (has("intention", "purpose", "reflection", "morning")) return "intention";
  if (has("action", "habit", "practice", "challenge")) return "action";
  if (has("speech", "poetry", "lyrics", "voice", "expression")) return "speech";
  return "view";
}

export const KIND_GLYPH: Record<ItemKind, string> = {
  music: "◐",
  wisdom: "✦",
  activation: "◇",
  book: "❖",
};
